// @flow

import React, { useState, useCallback, useRef, useLayoutEffect } from 'react';
import { pdfjs, Document, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import Tooltip from '@mui/material/Tooltip';
import useResizeObserver from '@react-hook/resize-observer';
import CircularProgress from '@mui/material/CircularProgress';
import { IconButton, Grid, Typography } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { getTextToHighlight, highlightText } from './highlighter';
import { getTextToHighlight as getTextToHighlightFuzzy, highlightText as highlightTextFuzzy } from './fuzzy-highlighter';
import Snackbar from '@mui/material/Snackbar';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const HIGHLIGHT_TEXT_SELECTOR =
  '.react-pdf__Page__textContent [role="presentation"]';
const TEXT_LAYER_SELECTOR = '.react-pdf__Page';
const CANVAS_SELECTOR = '.react-pdf__Page__canvas';
const CUSTOM_SQUARE_SELECTOR = '.custom-square';
const CUSTOM_SQUARE_CLASS_NAME = 'custom-square';
const PDF_DOCUMENT_SELECTOR = '.react-pdf__Document';

const autoScrollToFirstMark = () => {
  const firstMark = document.querySelector('span mark');

  if (firstMark) {
    firstMark.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
};

const useWidth = (target) => {
  const [width, setWidth] = useState(null);

  useLayoutEffect(() => {
    setWidth(target.current.getBoundingClientRect().width);
  }, [target]);

  useResizeObserver(target, (entry) => setWidth(entry.contentRect.width));

  return width;
};

type Props = {
  fileBin: Blob,
  fileName: String,
  previewPage: Number | null,
  boundingBox: Object | null,
  imageDimension: Object | null,
  searchText?: String | null,
  pdfFullHeight: Boolean,
  setPdfFullHeight: () => void
};

const PdfFileRenderer = (props: Props) => {
  const {
    fileName,
    fileBin,
    previewPage,
    boundingBox,
    imageDimension,
    searchText,
    pdfFullHeight,
    setPdfFullHeight
  } = props;
  const [scale, setScale] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(null);
  const [scanningAllPages, setScanningAllPages] = useState(false);
  const [snackbackOpen, setSnackbackOpen] = useState(false);
  const [snackbackMessage, setSnackbackMessage] = useState('');
  const pdfWrapper = useRef(null);
  const width = useWidth(pdfWrapper);

  const autoHighlight = useCallback(() => {
    const spanElements = document.querySelectorAll(HIGHLIGHT_TEXT_SELECTOR);
    const textToHighlight = getTextToHighlight(spanElements, searchText);
    const excerptFound = Boolean(Object.keys(textToHighlight)?.length);

    if (excerptFound) {
      highlightText(spanElements, textToHighlight);
      autoScrollToFirstMark();
      return true;
    }
    
    if (previewPage === pageNumber) {
      console.log('Trying fuzzy highlighter');
      const textToHighlightFuzzy = getTextToHighlightFuzzy(spanElements, searchText);
      
      if (textToHighlightFuzzy.length) {
        console.log('Fuzzy highlighter found an excerpt, highlighting now');
        highlightTextFuzzy(textToHighlightFuzzy);
        autoScrollToFirstMark();
        return true;
      }
    }

    return false;
  }, [searchText, previewPage, pageNumber]);

  const removeCustomSqure = useCallback(() => {
    const pdfDocument = document.querySelector(PDF_DOCUMENT_SELECTOR);

    if (pdfDocument) {
      const oldSquare = pdfDocument.querySelector(CUSTOM_SQUARE_SELECTOR);
      if (oldSquare) oldSquare.remove();
    }
  }, []);

  const drawBoundingBox = useCallback(() => {
    if (!boundingBox || !imageDimension) return false;
    const textLayer = document.querySelector(TEXT_LAYER_SELECTOR);
    const canvas = document.querySelector(CANVAS_SELECTOR);
    if (!textLayer || !canvas) return false;

    if (
      typeof boundingBox.x1 !== 'number' ||
      typeof boundingBox.y1 !== 'number' ||
      typeof boundingBox.x2 !== 'number' ||
      typeof boundingBox.y2 !== 'number' ||
      typeof imageDimension.width !== 'number' ||
      typeof imageDimension.height !== 'number'
    ) {
      return false;
    }

    const textLayerBoundingClientRect = textLayer.getBoundingClientRect();
    const textLayerWidth = textLayerBoundingClientRect.width;
    const textLayerHeight = textLayerBoundingClientRect.height;
    const canvasBoundingClientRect = canvas.getBoundingClientRect();
    const canvasWidth = canvasBoundingClientRect.width;
    const canvasHeight = canvasBoundingClientRect.height;
    const widthRatio = canvasWidth / textLayerWidth;
    const heightRatio = canvasHeight / textLayerHeight;
    const top = (boundingBox.y1 / imageDimension.height) * 100 * heightRatio;
    const left = (boundingBox.x1 / imageDimension.width) * 100 * widthRatio;
    const width =
      ((boundingBox.x2 - boundingBox.x1) / imageDimension.width) *
      100 *
      widthRatio;
    const height =
      ((boundingBox.y2 - boundingBox.y1) / imageDimension.height) *
      100 *
      heightRatio;
    const square = document.createElement('div');
    square.className = CUSTOM_SQUARE_CLASS_NAME;
    square.style.position = 'absolute';
    square.style.top = `${top}%`;
    square.style.left = `${left}%`;
    square.style.width = `${width}%`;
    square.style.height = `${height}%`;
    square.style.border = '1px solid red';
    square.style.background = 'rgba(255,44,33,0.1)';
    square.style.pointerEvents = 'none';
    square.style.zIndex = 10;
    textLayer.appendChild(square);
    square.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });

    return true;
  }, [boundingBox, imageDimension]);

  const onRenderTextLayerSuccessHandler = useCallback(() => {
    if (!searchText || !pageNumber) return;

    removeCustomSqure();

    if (previewPage === pageNumber) {
      if (drawBoundingBox()) return;
    }

    const excerptFound = autoHighlight();
    if (previewPage !== null || !scanningAllPages) return;

    if (excerptFound) {
      setSnackbackMessage('Excerpt found');
      setSnackbackOpen(true);
      setScanningAllPages(false);
    } else {
      if (pageNumber < numPages) {
        setPageNumber((prevPageNumber) => prevPageNumber + 1);
      } else {
        if (!excerptFound) {
          setSnackbackMessage('Excerpt not found');
          setSnackbackOpen(true);
          setPageNumber(1);
        }

        setScanningAllPages(false);
      }
    }
  }, [
    pageNumber,
    scanningAllPages,
    autoHighlight,
    numPages,
    previewPage,
    searchText
  ]);

  const onDocumentLoadSuccess = useCallback(
    async ({ numPages }) => {
      if (!previewPage && searchText) {
        setScanningAllPages(true);
        setSnackbackMessage('Searching for excerpt...');
        setSnackbackOpen(true);
      }

      setNumPages(numPages);
      setPageNumber(previewPage || 1);
    },
    [previewPage, searchText]
  );

  const downloadDocument = useCallback(() => {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(fileBin);
    link.download = `${fileName}.pdf`;
    link.click();
  }, [fileBin, fileName]);

  const changePage = useCallback((offset) => {
    setPageNumber((prevPageNumber) => prevPageNumber + offset);
  }, []);

  const previousPage = useCallback(() => {
    changePage(-1);
  }, [changePage]);

  const nextPage = useCallback(() => {
    changePage(1);
  }, [changePage]);

  const changeScale = useCallback((offset) => {
    setScale((prevScale) => prevScale + offset);
  }, []);

  const decreaseScale = useCallback(() => {
    changeScale(-0.1);
  }, [changeScale]);

  const increaseScale = useCallback(() => {
    changeScale(0.1);
  }, [changeScale]);

  const handleCloseSnackBar = useCallback(() => {
    setSnackbackOpen(false);
  }, []);

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={snackbackOpen}
        onClose={handleCloseSnackBar}
        autoHideDuration={5000}
        message={snackbackMessage}
      />
      <Grid item style={{ margin: 'auto' }}>
        <Typography variant="body2">
          <Tooltip title="Previous Page">
            <span>
              <IconButton
                aria-label="previous page"
                disabled={pageNumber <= 1}
                onClick={previousPage}
              >
                <ArrowBackIosIcon />
              </IconButton>
            </span>
          </Tooltip>
          {!scanningAllPages && (
            <>
              {pageNumber || (numPages ? 1 : '--')} / {numPages || '--'}
            </>
          )}
          <Tooltip title="Next Page">
            <span>
              <IconButton
                aria-label="next page"
                disabled={pageNumber >= numPages}
                onClick={nextPage}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Zoom Out">
            <span>
              <IconButton
                aria-label="zoom out"
                disabled={scale <= 0.5}
                onClick={decreaseScale}
              >
                <ZoomOutIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Zoom In">
            <span>
              <IconButton
                aria-label="zoom in"
                disabled={scale >= 2}
                onClick={increaseScale}
              >
                <ZoomInIcon />
              </IconButton>
            </span>
          </Tooltip>
          {pdfFullHeight ? (
            <Tooltip title="Exit Full Screen">
              <IconButton
                aria-label="exit full screen"
                onClick={() => setPdfFullHeight(false)}
              >
                <CloseFullscreenIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Switch to Full Screen">
              <IconButton
                aria-label="switch to full screen"
                onClick={() => setPdfFullHeight(true)}
              >
                <OpenInFullIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Download Document">
            <IconButton
              aria-label="download document"
              onClick={() => downloadDocument()}
            >
              <CloudDownloadIcon />
            </IconButton>
          </Tooltip>
        </Typography>
      </Grid>
      <Grid
        item
        style={{
          width: '100%',
          height: '100%',
          overflow: 'auto',
          border: '1px solid black'
        }}
        ref={pdfWrapper}
      >
        <Document
          style={{
            flexGrow: '1',
            overflow: 'auto',
            position: 'relative',
            width: '100%',
            height: '100%'
          }}
          file={fileBin}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={<CircularProgress />}
        >
          {scanningAllPages && (
            <div
              style={{
                position: 'absolute',
                width: width,
                height: '70%',
                backgroundColor: 'rgba(255, 255, 255, 1)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
              }}
            >
              <CircularProgress />
            </div>
          )}
          {pageNumber && (
            <Page
              scale={scale}
              loading={''}
              pageNumber={pageNumber}
              width={width}
              onRenderTextLayerSuccess={onRenderTextLayerSuccessHandler}
            />
          )}
        </Document>
      </Grid>
    </>
  );
};

export default PdfFileRenderer;
